/**
 * DTO du back-office (DECISIONS §4.2 — section Administration, jalon M3).
 *
 * Toute écriture admin est tracée dans `audit_logs` (acteur, avant, après) : les schémas
 * ci-dessous décrivent le corps attendu, la trace est produite par l'API.
 *
 * Les patchs sont **partiels et stricts** : on ne peut modifier qu'un champ connu du contenu, et
 * `id` n'est jamais modifiable (les identifiants de contenu sont immuables, DECISIONS §3).
 */
import { z } from 'zod';
import { emailSchema } from './models.js';
import { balanceSchema, carteSchema, niveauSchema } from '../content/index.js';
/** Corps de `POST /admin/auth/login`. */
export const adminLoginInputSchema = z
    .object({ email: emailSchema, password: z.string().min(1) })
    .strict();
/** Réponse de `POST /admin/auth/login` (`200`). */
export const adminLoginResponseSchema = z
    .object({
    accessToken: z.string().min(1),
    admin: z
        .object({ id: z.string().uuid(), email: z.string(), role: z.literal('admin') })
        .strict(),
})
    .strict();
/** Corps de `PATCH /admin/cards/:id` : tout champ de carte sauf `id`. */
export const adminCardPatchSchema = carteSchema.omit({ id: true }).partial().strict();
/** Corps de `PATCH /admin/balance` : n'importe quel sous-ensemble des paramètres d'équilibrage. */
export const adminBalancePatchSchema = balanceSchema.partial().strict();
/** Corps de `PATCH /admin/levels/:id` : tout champ de niveau sauf `id`. */
export const adminLevelPatchSchema = niveauSchema.omit({ id: true }).partial().strict();
// ---------------------------------------------------------------------------------------------
// Réponses de lecture (M3-06) — le back-office lit le **brouillon** courant, ou la version
// publiée quand aucun brouillon n'existe. Les formes sont identiques : un admin ne voit jamais
// un contenu partiel, seulement l'état éditable complet.
// ---------------------------------------------------------------------------------------------
/** Réponse de `GET /admin/cards` : cartes **complètes** (effets et synergies inclus). */
export const adminCardsResponseSchema = z.object({ cards: z.array(carteSchema) }).strict();
/** Réponse de `GET|PATCH /admin/cards/:id`. */
export const adminCardResponseSchema = z.object({ card: carteSchema }).strict();
/** Réponse de `GET|PATCH /admin/balance` : paramètres complets après fusion du patch. */
export const adminBalanceResponseSchema = z.object({ balance: balanceSchema }).strict();
/** Ligne de `GET /admin/levels` : résumé suffisant pour choisir un niveau à éditer. */
export const adminLevelResumeSchema = z
    .object({
    id: z.string(),
    numero: z.number().int().positive(),
    mode: z.enum(['limite', 'illimite']),
    da: z.enum(['feu', 'eau', 'terre', 'air']),
    nameKey: z.string(),
})
    .strict();
/** Réponse de `GET /admin/levels`. */
export const adminLevelsResponseSchema = z
    .object({ levels: z.array(adminLevelResumeSchema) })
    .strict();
/** Réponse de `GET|PATCH /admin/levels/:id`. */
export const adminLevelResponseSchema = z.object({ level: niveauSchema }).strict();
/**
 * Paramètres de `GET /admin/audit` : `limit` borné (1 à 200, défaut 50), `offset`.
 *
 * `adminPlayersQuerySchema` (limite 1 à 100) vit dans `validation.ts`, à côté des autres
 * paramètres de requête du contrat.
 */
export const adminAuditQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
})
    .strict();
/** Corps de `POST /admin/content/publish`. */
export const adminContentPublishInputSchema = z
    .object({
    /** Version à publier ; si absente, le serveur publie la version courante du bundle. */
    contentVersion: z.string().min(1).optional(),
})
    .strict();
/** Réponse de `POST /admin/content/publish` (`200`). */
export const adminContentPublishResponseSchema = z
    .object({ contentVersion: z.string(), publishedAt: z.string().datetime() })
    .strict();
/** Ligne du journal d'audit. */
export const auditLogSchema = z
    .object({
    id: z.string().uuid(),
    actorUserId: z.string().uuid().nullable(),
    action: z.string(),
    target: z.string(),
    avant: z.unknown().nullable(),
    apres: z.unknown().nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
/** Réponse de `GET /admin/audit` (`200`). */
export const auditListResponseSchema = z.object({ logs: z.array(auditLogSchema) }).strict();
/** Ligne de `GET /admin/players` (lecture seule, données minimisées). */
export const adminPlayerSchema = z
    .object({
    id: z.string().uuid(),
    pseudo: z.string(),
    /** Email masqué côté API (aucune adresse complète dans une liste, DECISIONS §11). */
    emailMasque: z.string(),
    locale: z.enum(['fr', 'en']),
    role: z.enum(['joueur', 'admin']),
    createdAt: z.string().datetime(),
    niveauMax: z.number().int().nonnegative(),
    eclats: z.number().int().nonnegative(),
    pointsCollection: z.number().int().nonnegative(),
    deletedAt: z.string().datetime().nullable(),
})
    .strict();
/** Réponse de `GET /admin/players` (`200`). */
export const adminPlayersResponseSchema = z
    .object({ players: z.array(adminPlayerSchema), total: z.number().int().nonnegative() })
    .strict();
//# sourceMappingURL=admin.js.map